/**
 * Turning "here is the session the AI wants" into "here is what to actually do".
 *
 * `applySessionCommands` returns a whole new session state. Handing that to
 * React wholesale would be wrong twice over, and both failures are silent:
 *
 *   - A track's volume, pan, mute and solo live on Web Audio nodes as well as
 *     in state. Replacing the array moves the fader in the UI and leaves the
 *     gain node exactly where it was, so the mix change is visible and
 *     inaudible. Those have to go through `updateTrack`, which drives both.
 *   - A pattern clip's voices are scheduled ahead of the playhead. Swapping the
 *     clip array does not unschedule them, so an edited groove keeps playing
 *     the old pattern until those voices run out.
 *
 * So this compares the state before and after and names the specific work:
 * which tracks changed and in what, which sends moved, which pattern clips
 * need their schedule dropped, and whether the tempo moved. It is pure, which
 * is the only reason any of the above can be tested without an audio context.
 */

/** Fields that reach the audio graph through updateTrack. */
const TRACK_AUDIO_FIELDS = ['volume', 'pan', 'muted', 'solo'];
/** Fields that are state-only, but still have to be carried across. */
const TRACK_STATE_FIELDS = ['name', 'output', 'fx', 'instrumentPatch', 'duck'];
/** A bus carries a chain and a level, and neither reaches the graph by itself. */
const BUS_FIELDS = ['name', 'volume', 'pan', 'fx'];

const byId = (items) => new Map((Array.isArray(items) ? items : []).map((item) => [item?.id, item]));

const changed = (left, right) => !Object.is(left, right);

const sendsDiff = (before = {}, after = {}) => {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const moves = [];
  keys.forEach((busId) => {
    const from = Number(before?.[busId]) || 0;
    const to = Number(after?.[busId]) || 0;
    if (from !== to) moves.push({ busId, level: to });
  });
  return moves;
};

const isPatternClip = (clip) => clip?.clipType === 'pattern';

/**
 * Samples a clip now references that it did not before.
 *
 * A sample reference is pure data, but the audio behind it is fetched and
 * decoded on demand — and that only happens when a clip is selected or a
 * session loads. An AI assignment fits neither, so without this the reference
 * would sit in the clip and the lane would keep playing its synthesised
 * fallback: the change would be visible in the sequencer and inaudible.
 */
const newSampleReferences = (before, after) => {
  const previous = before?.customSamples || {};
  const next = after?.customSamples || {};
  return Object.entries(next)
    .filter(([laneKey, sample]) => sample && previous[laneKey] !== sample)
    .map(([laneKey, sample]) => ({ laneKey, sample }));
};

/**
 * A pattern clip needs its scheduled voices dropped when anything that decides
 * what or when it plays has moved. A rename does not; a groove edit, a move or
 * a trim does.
 */
const patternPlaybackChanged = (before, after) => (
  changed(before.clipData, after.clipData)
  || changed(before.startBeat, after.startBeat)
  || changed(before.durationBeats, after.durationBeats)
  || changed(before.muted, after.muted)
);

export function planSessionEdit(before = {}, after = {}) {
  const beforeTracks = byId(before.tracks);
  const afterTracks = Array.isArray(after.tracks) ? after.tracks : [];

  const trackUpdates = [];
  const trackAdds = [];
  const sendUpdates = [];
  afterTracks.forEach((track) => {
    const previous = beforeTracks.get(track?.id);
    // A track nobody has built yet has no audio nodes and no synth engine
    // behind it. Updating fields on it would be updating nothing.
    if (!previous) {
      if (track?.id) trackAdds.push(track);
      return;
    }
    if (previous === track) return;

    const changes = {};
    [...TRACK_AUDIO_FIELDS, ...TRACK_STATE_FIELDS].forEach((field) => {
      if (changed(previous[field], track[field])) changes[field] = track[field];
    });
    if (Object.keys(changes).length > 0) trackUpdates.push({ id: track.id, changes });

    sendsDiff(previous.sends, track.sends).forEach((move) => {
      sendUpdates.push({ trackId: track.id, ...move });
    });
  });

  // A track that is gone needs its nodes and buffers torn down, not just its
  // record dropped from an array.
  const afterTrackIds = new Set(afterTracks.map((track) => track?.id));
  const trackRemovals = (Array.isArray(before.tracks) ? before.tracks : [])
    .filter((track) => track?.id && !afterTrackIds.has(track.id))
    .map((track) => track.id);

  const busUpdates = [];
  const busAdds = [];
  const beforeBuses = byId(before.buses);
  (Array.isArray(after.buses) ? after.buses : []).forEach((bus) => {
    const previous = beforeBuses.get(bus?.id);
    // A bus the edit created has to exist in the mixer before anything is sent to it.
    if (!previous && bus?.id) { busAdds.push(bus); return; }
    if (!previous || previous === bus) return;
    const changes = {};
    BUS_FIELDS.forEach((field) => {
      if (changed(previous[field], bus[field])) changes[field] = bus[field];
    });
    if (Object.keys(changes).length > 0) busUpdates.push({ id: bus.id, changes });
  });

  const beforeClips = byId(before.timelineClips);
  const afterClips = Array.isArray(after.timelineClips) ? after.timelineClips : [];
  const afterClipIds = new Set(afterClips.map((clip) => clip?.id));

  const rescheduleClipIds = [];
  const sampleLoads = [];
  afterClips.forEach((clip) => {
    if (!isPatternClip(clip)) return;
    const previous = beforeClips.get(clip.id);
    if (previous !== clip) {
      newSampleReferences(previous?.clipData, clip.clipData).forEach((reference) => {
        sampleLoads.push({ clipId: clip.id, ...reference });
      });
    }
    // A brand new pattern clip has nothing stale scheduled against its id.
    if (!previous || previous === clip) return;
    if (patternPlaybackChanged(previous, clip)) rescheduleClipIds.push(clip.id);
  });
  // A deleted clip's voices are still in flight and nothing else will stop them.
  (Array.isArray(before.timelineClips) ? before.timelineClips : []).forEach((clip) => {
    if (isPatternClip(clip) && !afterClipIds.has(clip?.id)) rescheduleClipIds.push(clip.id);
  });

  const clipsChanged = afterClips.length !== (Array.isArray(before.timelineClips) ? before.timelineClips : []).length
    || afterClips.some((clip) => beforeClips.get(clip?.id) !== clip);

  const beforeBpm = Number(before.bpm);
  const afterBpm = Number(after.bpm);
  const bpm = Number.isFinite(afterBpm) && afterBpm !== beforeBpm ? afterBpm : null;

  const automationChanged = JSON.stringify(before.automation || {}) !== JSON.stringify(after.automation || {});
  return {
    automationChanged,
    bpm,
    trackAdds,
    trackRemovals,
    trackUpdates,
    busAdds,
    busUpdates,
    sendUpdates,
    clipsChanged,
    rescheduleClipIds,
    sampleLoads,
    // Nothing to do means nothing to save, broadcast or push onto the undo stack.
    empty: bpm === null
      && trackAdds.length === 0
      && trackRemovals.length === 0
      && trackUpdates.length === 0
      && busAdds.length === 0
      && busUpdates.length === 0
      && sendUpdates.length === 0
      && !clipsChanged
      && !automationChanged
  };
}

export default planSessionEdit;
